/**
 * Las escenas del libro: el primer paso del plan (M-3, 3-oct-2026).
 *
 * Rais: «primero dividir en escenas numeradas — súper importante». El plan se escribe sobre escenas
 * («quita la 14», «reescribe la 31»), así que antes de pedirlo el libro tiene que estar partido.
 * Si el texto ya las separa («* * *»), no cuesta nada; si no, se lee capítulo a capítulo y propone
 * dónde empieza cada una. Lo que el autor une o parte manda, y sigue a su texto en las versiones
 * siguientes.
 *
 * El texto del libro no viaja a la pantalla: de cada escena, su sitio, su tamaño y sus primeras
 * palabras.
 */
import { useEffect, useState } from "react";
import { catalogoModelos, corregirEscenas, dividirEnEscenas, dolares, escenasDelLibro, lineasDeEscena, modelosAsignados } from "../api";
import type { EscenaDelLibro, MapaDeEscenas, ModeloCatalogo, Trabajo } from "../api";
import { avisoDeCapitulosSinLeer, avisoDeEscenasPerdidas, costeDeDividir, cuentaDeEscenas, escenasPorCapitulo, origenDeEscenas } from "../logica";
import { ListaDePasos } from "./ListaDePasos";

export default function Escenas({ carpeta, doc, trabajo, pulso, conPlan, alLanzar, alSaber }: {
  carpeta: string;
  doc: string | null;
  /** El último trabajo de dividir el libro, si lo hay. */
  trabajo: Trabajo | null;
  pulso: number;
  /** Ya hay plan: está escrito sobre estas escenas, así que se enseñan pero no se tocan. */
  conPlan: boolean;
  alLanzar: () => void;
  /** Si el libro está partido (`true`), si falta partirlo (`false`), o si no se sabe (`null`: no bloquea nada). */
  alSaber: (listas: boolean | null) => void;
}) {
  // `null`: aún no ha contestado. `undefined`: el motor no sabe de escenas.
  const [mapa, setMapa] = useState<MapaDeEscenas | null | undefined>(null);
  const [precio, setPrecio] = useState<ModeloCatalogo | undefined>(undefined);
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [sinRuta, setSinRuta] = useState(false);   // el motor no divide desde aquí (es anterior)
  const [abierta, setAbierta] = useState(false);
  // La escena que se está partiendo, con sus líneas.
  // `lineas`: null mientras se leen; "sin_ruta" si el motor no las da (es anterior).
  const [partiendo, setPartiendo] = useState<{ n: number; lineas: { i: number; texto: string }[] | null | "sin_ruta" } | null>(null);
  const enCurso = trabajo?.estado === "en_curso";

  const leer = () => {
    if (!carpeta || !doc) return;
    escenasDelLibro(carpeta, doc).then((m) => { setMapa(m); alSaber(m ? m.fiable : null); }).catch(() => { /* se conserva lo que había */ });
  };
  useEffect(leer, [carpeta, doc, pulso, trabajo?.estado]);
  useEffect(() => {
    Promise.all([modelosAsignados(), catalogoModelos()])
      .then(([m, c]) => setPrecio(c.modelos.find((x) => x.id === (m.porAgente ?? {}).reader)))
      .catch(() => {});
  }, []);

  async function dividir() {
    if (!doc) return;
    setOcupado(true); setFallo(null);
    try {
      const r = await dividirEnEscenas(carpeta, doc);
      if (r === undefined) setSinRuta(true);
      else if (r === "hecho") leer();
      else alLanzar();
    } catch (e) { setFallo((e as Error).message); }
    finally { setOcupado(false); }
  }
  async function tocar(cambio: { accion: "unir" | "partir"; i: number }) {
    if (!doc) return;
    setOcupado(true); setFallo(null);
    try { await corregirEscenas(carpeta, doc, cambio); setPartiendo(null); leer(); }
    catch (e) { setFallo((e as Error).message); }
    finally { setOcupado(false); }
  }
  async function abrirParaPartir(e: EscenaDelLibro) {
    if (!doc) return;
    if (partiendo?.n === e.n) { setPartiendo(null); return; }
    setPartiendo({ n: e.n, lineas: null });
    const lineas = await lineasDeEscena(carpeta, doc, e.n).catch(() => null);
    setPartiendo((p) => (p?.n === e.n ? { n: e.n, lineas: lineas ?? "sin_ruta" } : p));
  }

  if (mapa === undefined) return null;   // un motor sin escenas: el plan sigue por capítulos, como antes
  if (!mapa) {
    return <section className="carta"><h2>Las escenas de tu libro</h2><p className="vacio">Mirando el manuscrito…</p></section>;
  }

  const palabras = mapa.escenas.reduce((n, e) => n + e.palabras, 0);
  const capitulos = new Set(mapa.escenas.map((e) => e.capitulo)).size;

  // Todavía sin partir: una escena por capítulo, de momento.
  if (!mapa.fiable) {
    const coste = costeDeDividir(palabras, capitulos, precio);
    return (
      <section className="carta">
        <h2>1 · Las escenas de tu libro<span className="sub">{enCurso ? "leyéndolo" : "sin dividir todavía"}</span></h2>
        <p className="vacio">
          El plan se escribe sobre escenas numeradas: «quita la 14», «reescribe la 31». Tu manuscrito no las separa, así que
          primero hay que dividirlo: se lee capítulo a capítulo y propone dónde empieza cada una. Después puedes unir o partir las que quieras.
        </p>
        {trabajo && trabajo.estado !== "hecho" && <ListaDePasos trabajo={trabajo} />}
        {trabajo?.estado === "fallido" && <p className="aviso" style={{ borderColor: "var(--acc)" }}><b style={{ color: "var(--acc)" }}>No se ha podido dividir.</b> {trabajo.error}</p>}
        {enCurso ? (
          <p className="vacio">
            Leyendo{trabajo?.progreso ? ` el capítulo ${Math.min(trabajo.progreso.hecho + 1, trabajo.progreso.total)} de ${trabajo.progreso.total}` : "…"}.
            Puedes cerrar el Studio: sigue en tu ordenador.
          </p>
        ) : sinRuta ? (
          <p className="aviso">Tu motor todavía no divide el libro desde aquí. Actualízalo en Ajustes; mientras tanto, el plan se hace por capítulos.</p>
        ) : (
          <div className="botones">
            <button className="btn" disabled={!doc || ocupado} onClick={dividir}>Dividir en escenas</button>
            <span className="vacio">
              {capitulos} {capitulos === 1 ? "capítulo" : "capítulos"} · ~{palabras.toLocaleString("es-ES")} palabras{coste != null ? ` · ~${dolares(coste)} de tu llave` : ""}
            </span>
          </div>
        )}
        {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
      </section>
    );
  }

  const grupos = escenasPorCapitulo(mapa.escenas);
  const perdidas = avisoDeEscenasPerdidas(mapa.perdidos);
  const sinLeer = avisoDeCapitulosSinLeer(mapa.sin_leer);
  return (
    <section className="carta">
      <h2>1 · Las escenas de tu libro<span className="sub">{cuentaDeEscenas(mapa.escenas)} · {origenDeEscenas(mapa.fuente)}</span></h2>
      <p className="vacio">
        {conPlan
          ? "El plan está escrito sobre estas escenas. Si necesitas cambiarlas, rehaz el plan después."
          : "El plan se escribe sobre estas escenas, por su número. Míralas: si dos son una sola, únelas; si una son dos, pártela."}
      </p>
      {perdidas && <p className="aviso">{perdidas}</p>}
      {/* Un capítulo que no se pudo leer no es «ya está dividido»: se dice cuál, y se lee solo ese. */}
      {sinLeer && (
        <div className="aviso">
          {sinLeer}
          {enCurso ? (
            <p className="vacio">
              Leyendo{trabajo?.progreso ? ` el capítulo ${Math.min(trabajo.progreso.hecho + 1, trabajo.progreso.total)} de ${trabajo.progreso.total}` : "…"}. Puedes cerrar el Studio: sigue en tu ordenador.
            </p>
          ) : sinRuta ? (
            <p className="vacio">Tu motor todavía no lee los capítulos sueltos desde aquí. Actualízalo en Ajustes.</p>
          ) : !conPlan ? (
            <>
              {trabajo?.estado === "fallido" && <p className="vacio" style={{ color: "var(--acc)" }}>No se ha podido leer. {trabajo.error}</p>}
              <div className="botones">
                <button className="btn" disabled={!doc || ocupado} onClick={dividir}>{(mapa.sin_leer?.length ?? 0) === 1 ? "Leer el que falta" : "Leer los que faltan"}</button>
                <span className="vacio">Solo se leen esos; los demás no se pagan otra vez. También puedes partirlos a mano.</span>
              </div>
            </>
          ) : null}
        </div>
      )}
      <div className="botones">
        <button className="btn fantasma" aria-expanded={abierta} onClick={() => setAbierta(!abierta)}>{abierta ? "Ocultar las escenas" : "Ver las escenas"}</button>
      </div>
      {abierta && (
        <div className="mapa-escenas">
          {grupos.map((g) => (
            <div key={g.capitulo} className="capitulo-escenas">
              <div className="cab">{g.titulo}<span className="sub">{g.escenas.length} {g.escenas.length === 1 ? "escena" : "escenas"}</span></div>
              <ol>
                {g.escenas.map((e, k) => (
                  <li key={e.n}>
                    <div className="fila">
                      <span className="num">{e.n}</span>
                      <span className="arranque">{e.arranque}{e.arranque.length >= 158 ? "…" : ""}</span>
                      <span className="sub">{e.palabras.toLocaleString("es-ES")} palabras{k > 0 && e.motivo ? ` · ${e.motivo}` : ""}</span>
                    </div>
                    {!conPlan && (
                      <div className="acciones">
                        {k > 0 && <button className="btn texto" disabled={ocupado} onClick={() => tocar({ accion: "unir", i: e.desde })}>Unir con la anterior</button>}
                        <button className="btn texto" disabled={ocupado} aria-expanded={partiendo?.n === e.n} onClick={() => abrirParaPartir(e)}>{partiendo?.n === e.n ? "No partir" : "Partir"}</button>
                      </div>
                    )}
                    {partiendo?.n === e.n && (
                      partiendo.lineas == null ? <p className="vacio">Leyendo la escena…</p>
                        // Un motor anterior no enseña los párrafos de una escena: se dice eso, no «es un solo párrafo».
                        : partiendo.lineas === "sin_ruta" ? <p className="vacio">Tu motor todavía no deja partir una escena desde aquí. Actualízalo en Ajustes.</p>
                        : partiendo.lineas.length < 2 ? <p className="vacio">Esta escena es un solo párrafo: no hay por dónde partirla.</p>
                        : (
                          <ul className="lineas">
                            <li className="vacio">Elige el párrafo donde empieza la escena nueva:</li>
                            {partiendo.lineas.slice(1).map((l) => (
                              <li key={l.i}><button className="btn texto" disabled={ocupado} onClick={() => tocar({ accion: "partir", i: l.i })}>{l.texto}{l.texto.length >= 138 ? "…" : ""}</button></li>
                            ))}
                          </ul>
                        )
                    )}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
    </section>
  );
}
